import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { dailyLogsApi, type DailyLog, type LogMedia } from '@/api/endpoints';
import {
  Alert,
  Button,
  DataTable,
  DetailSection,
  Drawer,
  EmptyState,
  FilterBar,
  ListGroup,
  ListRow,
  Select,
  Spinner,
  TableRow,
} from '@/components/ui';
import { FullscreenViewer } from '@/features/drawings/FullscreenViewer';
import { LazyMediaViewer } from '@/features/drawings/LazyViewer';
import { RecordFlag, RecordTime } from '@/features/records/RecordTime';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/**
 * 日报列表（「本工地」，CR-082 重做）。
 *
 * - **电脑 xl 以上是表格**：日期、填写人、内容（唯一截断的一列）、附件、提交时间；点一行右侧滑出详情，列表不动。
 * - **窄屏和手机是按日期分组的列表**：组名「9/22 周二」，行 = 人 + 两行摘要 + 右边时间和照片数；点一行底部弹出详情。
 * - **工具条在这里画**：左边是页面给的筛选（`toolbar`），右边是汇总 —— 总数来自服务端的分页结果；
 *   照片、视频只数本页，所以写「本页」（CR-031：拿本页的数字冒充整个区间，是另一种静默说谎）。
 *
 * CR-022 起「找到要看的那一条」和「看那一条的细节」分两步；CR-082 把第二步从行内展开换成详情面板。
 * CR-023 删掉的：任务名、自定义字段、状态、审核区 —— 日报不再有审核流，它就是一条流水记录。
 */
export function LogList({
  siteId,
  timezone,
  from,
  to,
  toolbar,
}: {
  siteId: number;
  /** 工地时区。`createdAt` 是真时刻，必须按它渲染（CR-033） */
  timezone: string;
  from?: string;
  to?: string;
  /** 工具条左边：本工地 / 我的、日期区间 */
  toolbar?: ReactNode;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [openId, setOpenId] = useState<string | null>(null);

  const params = {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const logs = useQuery({
    queryKey: ['daily-logs', siteId, params, page, pageSize],
    queryFn: () => dailyLogsApi.list(siteId, { ...params, page, pageSize }),
  });

  const data = logs.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const items = data?.items ?? [];
  const pagePhotos = items.reduce((n, log) => n + log.photos.filter((m) => !isVideo(m)).length, 0);
  const pageVideos = items.reduce((n, log) => n + log.photos.filter(isVideo).length, 0);
  const opened = items.find((log) => log.id === openId) ?? null;

  const summary = data && data.total > 0 && (
    <span className="flex flex-wrap gap-x-4">
      <span>{t('logs.totalCount', { count: data.total })}</span>
      {pagePhotos > 0 && <span>{t('logs.pagePhotos', { count: pagePhotos })}</span>}
      {pageVideos > 0 && <span>{t('logs.pageVideos', { count: pageVideos })}</span>}
    </span>
  );

  return (
    <>
      <FilterBar summary={summary}>{toolbar}</FilterBar>

      {logs.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {logs.isError && <Alert>{toMessage(logs.error)}</Alert>}
      {data && data.total === 0 && <EmptyState title={t('logs.empty')} />}

      {data && data.total > 0 && (
        <>
          {/* 窄屏：按日期分组的列表。表格放不下就不出现（CR-036） */}
          <div className="flex flex-col gap-4 xl:hidden">
            {groupByDay(items).map(([day, logsOfDay]) => (
              <ListGroup key={day} title={<DayLabel workDate={logsOfDay[0]!.workDate} />}>
                {logsOfDay.map((log) => (
                  <LogListRow key={log.id} log={log} timezone={timezone} onOpen={() => setOpenId(log.id)} />
                ))}
              </ListGroup>
            ))}
          </div>

          {/* 电脑：表格。列宽由表头定死，只有「内容」一列吃剩下的宽度、允许截断（CR-035） */}
          <DataTable
            minWidth="52rem"
            columns={[
              { key: 'date', label: t('logs.workDate'), className: 'w-32' },
              { key: 'user', label: t('logs.submitter'), className: 'w-32' },
              { key: 'content', label: t('logs.content') },
              { key: 'media', label: t('logs.media'), className: 'w-28' },
              { key: 'submitted', label: t('records.submittedColumn'), className: 'w-44' },
            ]}
          >
            {items.map((log) => (
              <TableRow key={log.id} selected={openId === log.id} onOpen={() => setOpenId(log.id)}>
                <td className="whitespace-nowrap tabular-nums text-ink-2">
                  <DayLabel workDate={log.workDate} />
                </td>
                <td className="whitespace-nowrap font-medium text-ink">{log.user?.name}</td>
                <td className="truncate text-ink-2">{log.content}</td>
                <td className="whitespace-nowrap text-caption text-ink-3">
                  <MediaCount media={log.photos} />
                </td>
                <td className="whitespace-nowrap">
                  <RecordTime recordedAt={log.createdAt} timeZone={timezone} businessDay={log.workDate} bare />
                </td>
              </TableRow>
            ))}
          </DataTable>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <label className="text-caption text-ink-3" htmlFor="log-page-size">
                {t('app.perPage')}
              </label>
              <Select
                id="log-page-size"
                className="w-20"
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  // 改每页数量必须回第一页，否则可能落在一个不存在的页码上
                  setPage(1);
                }}
              >
                {[20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
              <span className="text-caption text-ink-3">
                {t('app.pageSummary', {
                  from: (data.page - 1) * data.pageSize + 1,
                  to: Math.min(data.page * data.pageSize, data.total),
                  total: data.total,
                })}
              </span>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  {t('app.prev')}
                </Button>
                <span className="min-w-14 text-center text-caption tabular-nums text-ink-3">
                  {data.page} / {totalPages}
                </span>
                <Button variant="secondary" size="sm" disabled={data.page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  {t('app.next')}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <LogDetail log={opened} timezone={timezone} onClose={() => setOpenId(null)} />
    </>
  );
}

/** 列表的一行（手机、窄屏）：人 + 补录标签，两行摘要；右边提交时刻和照片数 */
export function LogListRow({
  log,
  timezone,
  onOpen,
  showUser = true,
}: {
  log: DailyLog;
  timezone: string;
  onOpen: () => void;
  /** 「我的」里人都是自己，第一行写日期 */
  showUser?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const time = new Date(log.createdAt).toLocaleTimeString(i18n.language, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return (
    <ListRow
      onClick={onOpen}
      title={
        <span className="inline-flex items-center gap-2">
          {showUser ? log.user?.name : <DayLabel workDate={log.workDate} />}
          <RecordFlag recordedAt={log.createdAt} timeZone={timezone} businessDay={log.workDate} />
        </span>
      }
      excerpt={log.content || t('logs.noContent')}
      trailing={
        <>
          <span className="tabular-nums">{time}</span>
          <MediaCount media={log.photos} />
        </>
      }
    />
  );
}

/**
 * 一条日报的详情（CR-082）：电脑右侧、手机底部。正文全文、照片和视频、提交时间（补录的写明）。
 * 「本工地」和「我的」共用。
 */
export function LogDetail({
  log,
  timezone,
  onClose,
}: {
  log: DailyLog | null;
  timezone: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [viewing, setViewing] = useState<LogMedia | null>(null);
  const day = log ? formatDay(log.workDate, i18n.language) : '';

  return (
    <>
      <Drawer
        open={log !== null}
        onClose={onClose}
        title={t('logs.detailTitle', { date: day })}
        subtitle={
          log && (
            <span className="inline-flex flex-wrap items-center gap-x-2">
              {log.user?.name && <span>{log.user.name}</span>}
              <RecordTime recordedAt={log.createdAt} timeZone={timezone} businessDay={log.workDate} />
            </span>
          )
        }
      >
        {log && (
          <div className="flex flex-col gap-5">
            <p className="whitespace-pre-wrap text-body leading-relaxed text-ink">
              {log.content || <span className="text-ink-3">{t('logs.noContent')}</span>}
            </p>
            {log.photos.length > 0 && (
              <DetailSection title={t('logs.media')}>
                <MediaGrid media={log.photos} onOpenPhoto={setViewing} />
              </DetailSection>
            )}
          </div>
        )}
      </Drawer>

      {viewing && log && (
        <FullscreenViewer
          title={log.user?.name ?? ''}
          meta={
            <>
              {day}
              {viewing.takenAt && (
                <span className="ml-2">
                  {t('records.capturedAt', {
                    time: new Date(viewing.takenAt).toLocaleString(i18n.language, {
                      timeZone: timezone,
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  })}
                </span>
              )}
            </>
          }
          onClose={() => setViewing(null)}
        >
          <LazyMediaViewer url={viewing.url} mimeType={viewing.mimeType ?? 'image/jpeg'} />
        </FullscreenViewer>
      )}
    </>
  );
}

/** 「9/22 周二」。日期型的值按 UTC 渲染，按工地时区会整列退一天（CR-031） */
export function DayLabel({ workDate }: { workDate: string }) {
  const { i18n } = useTranslation();
  return <>{formatDay(workDate, i18n.language)}</>;
}

function formatDay(workDate: string, language: string): string {
  const date = new Date(workDate);
  const md = date.toLocaleDateString(language, { timeZone: 'UTC', month: 'numeric', day: 'numeric' });
  const weekday = date.toLocaleDateString(language, { timeZone: 'UTC', weekday: 'short' });
  return `${md} ${weekday}`;
}

/** 按日期分组，保持服务端给的顺序（新的在前） */
export function groupByDay(logs: readonly DailyLog[]): Array<[string, DailyLog[]]> {
  const groups: Array<[string, DailyLog[]]> = [];
  for (const log of logs) {
    const key = log.workDate.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last[0] === key) last[1].push(log);
    else groups.push([key, [log]]);
  }
  return groups;
}

/** 「2 张照片 · 1 段视频」。不用 📷 🎬（客户不要图标，CR-081） */
function MediaCount({ media }: { media: LogMedia[] }) {
  const { t } = useTranslation();
  const photos = media.filter((m) => !isVideo(m)).length;
  const videos = media.filter(isVideo).length;
  if (photos === 0 && videos === 0) return <span className="text-ink-4">—</span>;
  return (
    <span className="whitespace-nowrap">
      {[photos > 0 && t('logs.photoCount', { count: photos }), videos > 0 && t('logs.videoCount', { count: videos })]
        .filter(Boolean)
        .join(' · ')}
    </span>
  );
}

/**
 * 日报的图片和视频。
 *
 * 视频**不做封面图**（服务端不跑 ffmpeg），所以直接给 `<video controls>` ——
 * `preload="metadata"` 只拉文件头，一屏里有几段视频也不会把流量打满。
 * 点图片开灯箱，视频就地播：视频的全屏交给浏览器自己的播放器，比自己写一个可靠。
 */
export function MediaGrid({
  media,
  onOpenPhoto,
}: {
  media: LogMedia[];
  onOpenPhoto: (item: LogMedia) => void;
}) {
  if (media.length === 0) return null;

  return (
    <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {media.map((item) =>
        isVideo(item) ? (
          <li key={item.id} className="col-span-full">
            <video
              src={item.url}
              controls
              preload="metadata"
              playsInline
              className="aspect-video w-full rounded-lg border border-rule bg-black"
            />
          </li>
        ) : (
          <li key={item.id}>
            <button type="button" onClick={() => onOpenPhoto(item)} className="block w-full">
              <Thumb item={item} />
            </button>
          </li>
        ),
      )}
    </ul>
  );
}

/**
 * 一张缩略图，**加载失败时说出来**（CR-037）。
 *
 * 改之前失败的 `<img>` 只是一个空白方块 —— 客户在手机上看到的就是它，
 * 他只能描述成「像是链接错误」。照片地址是对象存储的签名地址，
 * 在工地弱网、地址过期、或者 WebView 拦掉明文请求时都会取不到，
 * 而这三件事里有两件是我们能修的 —— 前提是屏幕上得先看得出来它失败了。
 */
function Thumb({ item }: { item: LogMedia }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-dashed border-rule px-1 text-center text-micro leading-tight text-ink-3">
        {t('logs.mediaLoadFailed')}
      </span>
    );
  }

  return (
    <img
      src={item.thumbUrl ?? item.url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="aspect-[4/3] w-full rounded-lg border border-rule object-cover"
    />
  );
}

/**
 * 是不是视频。
 *
 * 看 `kind`，`mimeType` 只是兜底 —— CR-023 之前入库的附件全是 `kind: 'PHOTO'`，
 * 那时候还不收视频，所以这个兜底实际只会对将来某个 kind 没写对的情况起作用。
 */
export function isVideo(item: LogMedia): boolean {
  return item.kind === 'VIDEO' || Boolean(item.mimeType?.startsWith('video/'));
}
